/**
 * @fileoverview Contexto responsável por gerenciar conexões Socket.IO reutilizáveis e seguras por empresa e usuário.
 */

import { createContext } from "react";
import openSocket from "socket.io-client";
import { jwtDecode } from "jwt-decode";

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.joins = [];
    this.connectHandler = null;
    this.refreshJoinsOnReady = null;

    // Guard: não configurar listeners se rawSocket for null
    if (!this.rawSocket) {
      return;
    }

    this.connectHandler = () => {
      if (!this.rawSocket.recovered) {
        this.refreshJoinsOnReady = () => {
          for (const j of this.joins) {
            this.rawSocket.emit(`join${j.event}`, ...j.params);
          }
          this.rawSocket.off("ready", this.refreshJoinsOnReady);
          this.refreshJoinsOnReady = null;
        };
        for (const j of this.callbacks) {
          this.rawSocket.off(j.event, j.callback);
          this.rawSocket.on(j.event, j.callback);
        }

        this.rawSocket.on("ready", this.refreshJoinsOnReady);
      }
    };

    this.rawSocket.on("connect", this.connectHandler);
  }
  
  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback);
    }
    if (!this.rawSocket) return;
    this.callbacks.push({event, callback});
    return this.rawSocket.on(event, callback);
  }

  off(event, callback) {
    if (!this.rawSocket) return;

    if (!callback) {
      const eventCallbacks = this.callbacks.filter((c) => c.event === event);
      for (const c of eventCallbacks) {
        this.rawSocket.off(c.event, c.callback);
      }
      this.callbacks = this.callbacks.filter((c) => c.event !== event);
      return;
    }

    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback);
    if (i !== -1) {
      this.callbacks.splice(i, 1);
    }
    return this.rawSocket.off(event, callback);
  }

  emit(event, ...params) {
    if (!this.rawSocket) return;
    if (event.startsWith("join")) {
      this.joins.push({ event: event.substring(4), params });
    }
    return this.rawSocket.emit(event, ...params);
  }

  disconnect() {
    if (!this.rawSocket) return;

    if (this.refreshJoinsOnReady) {
      this.rawSocket.off("ready", this.refreshJoinsOnReady);
      this.refreshJoinsOnReady = null;
    }

    if (this.connectHandler) {
      this.rawSocket.off("connect", this.connectHandler);
      this.connectHandler = null;
    }

    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params);
    }
    this.joins = [];

    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
    this.rawSocket = null;
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,

  getSocket: function(companyId) {
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        if (process.env.NODE_ENV !== 'production') {
          // console.warn("closing old socket - company or user changed");
        }
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
      }

      let token = localStorage.getItem("token");
      if (!token || typeof token !== 'string') {
        return new DummySocket();
      }
      
      let exp;
      try {
        const decoded = jwtDecode(token);
        exp = decoded.exp;
        if (!exp) {
          return new DummySocket();
        }
      } catch (error) {
        console.warn("Token inválido:", error);
        return new DummySocket();
      }

      if ( Date.now() >= exp*1000) {
        if (process.env.NODE_ENV !== 'production') {
          // console.warn("Expired token, reload after refresh");
        }
        setTimeout(() => {
          window.location.reload();
        },1000);
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      
      if (!token) {
        return new DummySocket();
      }
      
      this.currentSocket = openSocket(process.env.REACT_APP_BACKEND_URL, {
        transports: ["websocket", "polling"], // WebSocket first, polling fallback
        upgrade: true,                         // Enable automatic upgrade
        pingTimeout: 120000,                   // Sync with backend (120s)
        pingInterval: 30000,                   // Sync with backend (30s)
        query: { token },
      });
      
      this.currentSocket.on("disconnect", (reason) => {
        if (reason.startsWith("io ")) {
          let exp;
          try {
            const decoded = jwtDecode(token);
            exp = decoded.exp;
            if (!exp) {
              return;
            }
          } catch (error) {
            console.warn("Token inválido:", error);
            return;
          }
          if ( Date.now()-180 >= exp*1000) {
            window.location.reload();
            return;
          }
          this.currentSocket.connect();
        }
      });

      // Muta logs de eventos em produção/dev para evitar vazamento de dados sensíveis
      this.currentSocket.onAny(() => {});
      
      this.onReady(() => {
        this.socketReady = true;
      });

    }
    
    const managed = new ManagedSocket(this);
    // expõe para casos onde precisamos emitir fora de hooks/contexts
    if (typeof window !== 'undefined') {
      window.currentSocketManager = this;
    }
    return managed;
  },
  
  onReady: function( callbackReady ) {
    if (this.socketReady) {
      callbackReady();
      return
    }
    
    this.currentSocket.once("ready", () => {
      callbackReady();
    });
  },

};

const SocketContext = createContext()

export { SocketContext, SocketManager };
